"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

type Tab = "Unit/Integration" | "Visual Regression" | "Accessibility";

export default function TestingPlayground() {
  const [activeTab, setActiveTab] = useState<Tab>("Unit/Integration");

  return (
    <div className="mt-4 flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
      <div className="flex border-b border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-900/50">
        {(
          ["Unit/Integration", "Visual Regression", "Accessibility"] as Tab[]
        ).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`relative flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === tab
                ? "text-blue-600 dark:text-blue-400"
                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            }`}
          >
            {activeTab === tab && (
              <motion.div
                layoutId="testing-tab-indicator"
                className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400"
                initial={false}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              />
            )}
            {tab}
          </button>
        ))}
      </div>

      <div className="min-h-[300px] flex-1 bg-gray-50/50 p-6 dark:bg-black/20">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            {activeTab === "Unit/Integration" && <VitestSimulator />}
            {activeTab === "Visual Regression" && <VisualDiffSlider />}
            {activeTab === "Accessibility" && <AxeSimulator />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function VitestSimulator() {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runTests = () => {
    if (running) return;
    setRunning(true);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) {
          clearInterval(interval);
          setRunning(false);
          return 100;
        }
        return p + 20;
      });
    }, 300);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between rounded-md bg-gray-900 p-2">
        <div className="flex space-x-2 px-2">
          <div className="h-3 w-3 rounded-full bg-red-500"></div>
          <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
          <div className="h-3 w-3 rounded-full bg-green-500"></div>
        </div>
      </div>
      <div className="relative flex flex-1 flex-col overflow-hidden rounded-lg bg-gray-900 p-4 font-mono text-sm shadow-inner">
        <div className="mb-4 text-gray-400">$ vitest run components</div>

        {progress > 0 && (
          <div className="space-y-2 text-gray-300">
            <div
              className={progress >= 20 ? "text-green-400" : "text-gray-600"}
            >
              {progress >= 20 ? "✓" : "○"} Button.test.tsx{" "}
              <span className="text-xs text-gray-500">
                {progress >= 20 ? "12ms" : ""}
              </span>
            </div>
            <div
              className={progress >= 40 ? "text-green-400" : "text-gray-600"}
            >
              {progress >= 40 ? "✓" : "○"} Input.test.tsx{" "}
              <span className="text-xs text-gray-500">
                {progress >= 40 ? "34ms" : ""}
              </span>
            </div>
            <div
              className={progress >= 60 ? "text-green-400" : "text-gray-600"}
            >
              {progress >= 60 ? "✓" : "○"} Modal.test.tsx{" "}
              <span className="text-xs text-gray-500">
                {progress >= 60 ? "21ms" : ""}
              </span>
            </div>
            <div
              className={progress >= 80 ? "text-green-400" : "text-gray-600"}
            >
              {progress >= 80 ? "✓" : "○"} Select.test.tsx{" "}
              <span className="text-xs text-gray-500">
                {progress >= 80 ? "18ms" : ""}
              </span>
            </div>
          </div>
        )}

        {progress === 100 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 flex flex-col gap-1 border-t border-gray-700/50 pt-4"
          >
            <div className="font-bold text-green-400">
              Test Files 4 passed (4)
            </div>
            <div className="font-bold text-green-400">Tests 24 passed (24)</div>
            <div className="text-gray-400">
              Start at {new Date().toLocaleTimeString()}
            </div>
            <div className="text-gray-400">Duration 1.24s</div>
          </motion.div>
        )}
      </div>

      <button
        onClick={runTests}
        disabled={running}
        className="self-end rounded-lg bg-blue-600 px-4 py-2 font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
      >
        {running ? "Running..." : "Run Test Suite"}
      </button>
    </div>
  );
}

function VisualDiffSlider() {
  return (
    <div className="flex h-full flex-col items-center justify-center space-y-6 text-center">
      <div className="relative grid h-32 w-full max-w-md grid-cols-2 divide-x divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-zinc-700 dark:border-zinc-700 dark:bg-zinc-800">
        <div className="group relative flex flex-col items-center justify-center p-4">
          <span className="absolute left-2 top-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">
            Baseline
          </span>
          <button className="rounded bg-blue-600 px-4 py-2 text-white shadow-sm">
            Checkout
          </button>
        </div>
        <div className="group relative flex flex-col items-center justify-center bg-red-50/50 p-4 dark:bg-red-900/10">
          <span className="absolute right-2 top-2 text-[10px] font-bold uppercase tracking-wider text-red-500">
            Current PR
          </span>
          {/* Visual Regression: Button Padding has changed, padding-y is smaller */}
          <button className="relative rounded border border-red-400 bg-blue-600 px-4 py-1.5 text-white shadow-sm ring-2 ring-red-400 ring-opacity-20">
            Checkout
          </button>
        </div>
      </div>
      <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
        Visuelle Regression Tests vergleichen das aktuelle DOM-Rendering Pixel
        für Pixel mit dem Baseline-Screenshot des Main-Branches. Hier hat jemand
        versehentlich das{" "}
        <code className="rounded bg-gray-100 px-1 text-red-500 dark:bg-zinc-800">
          padding-y
        </code>{" "}
        verringert!
      </p>
    </div>
  );
}

function AxeSimulator() {
  return (
    <div className="flex h-full flex-col space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-zinc-700 dark:bg-zinc-800">
        <form
          className="space-y-4"
          onSubmit={(e) => e.preventDefault()}
          {...({
            toolname: "a11y_inspection_demo_form",
            tooldescription:
              "Demonstrationsformular zur Analyse von typischen Barrierefreiheitsfehlern im Axe-Simulator",
          } as any)}
        >
          <div>
            {/* Intentional a11y issue: no label for input */}
            <input
              type="text"
              name="demo_search"
              placeholder="Search..."
              className="w-full rounded-md border px-3 py-2"
              {...({
                toolparamdescription: "Demo-Suchfeld ohne Label zur Veranschaulichung",
              } as any)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" id="terms" />
            <span className="text-sm">I agree</span>{" "}
            {/* Missing label htmlFor */}
          </div>
          {/* Intentional a11y issue: color contrast issue */}
          <button className="rounded bg-gray-200 px-4 py-2 font-bold text-gray-400">
            Submit
          </button>
        </form>
      </div>

      <div className="flex-1 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/30 dark:bg-red-900/10">
        <h4 className="mb-3 flex items-center gap-2 font-bold tracking-tight text-red-700 dark:text-red-400">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
          </svg>
          Accessibility Violations Found (3)
        </h4>
        <ul className="space-y-3 text-sm">
          <li className="flex flex-col text-red-800 dark:text-red-300">
            <span className="font-semibold">color-contrast</span>
            <span className="text-red-600/80 dark:text-red-400/80">
              Elements must have sufficient color contrast. (Submit Button)
            </span>
          </li>
          <li className="flex flex-col text-red-800 dark:text-red-300">
            <span className="font-semibold">label</span>
            <span className="text-red-600/80 dark:text-red-400/80">
              Form elements must have labels. (&lt;input placeholder="Search..."
              /&gt;)
            </span>
          </li>
          <li className="flex flex-col text-red-800 dark:text-red-300">
            <span className="font-semibold">label</span>
            <span className="text-red-600/80 dark:text-red-400/80">
              Form elements must have labels. (&lt;input type="checkbox"
              id="terms" /&gt;)
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}
